/* =========================================================
   Flexi Digital Onboarding — TS models
   Shapes mirror the backend DTOs in
   Backend/Kuba2.Client.Entities/Flexi/Onboarding/.
   ========================================================= */

/** Status byte returned by the backend (matches FlexiOnboardingStatus enum). */
export enum FlexiOnboardingStatus {
  NotStarted = 0,
  InProgress = 1,
  Completed = 2,
}

/** Canonical step key identifiers for the seeded regulatory catalog. */
export const DEFAULT_STEP_KEYS = [
  'OhsAgreement',
  'SystematicHseWork',
  'SafetyRepAndEmployeeParticipation',
  'AssessmentOfAdequateTrainingDuration',
  'RiskAssessmentAndExposure',
  'PsychosocialWorkEnvironment',
  'TargetedAndRiskBasedHealthMonitoring',
  'DeviationHandling',
  'RegulatoryChanges',
  'ActionPlanInKubaIK',
] as const;
export type DefaultStepKey = typeof DEFAULT_STEP_KEYS[number];

/** Matches FlexiOnboardingStepResponse. */
export interface FlexiOnboardingStep {
  id: number;
  stepKey: string;
  position: number;
  isDefault: boolean;
  title: string;
  shortLabel: string;
  subtitle: string | null;
  templateText: string | null;
  editableText: string | null;
  status: FlexiOnboardingStatus;
}

/**
 * Matches FlexiOnboardingSummaryResponse — one row of the history grid.
 *
 * A business holds a list of these; each is an independent version of the
 * Responsibilities and Duties form with its own audit trail and PDF.
 */
export interface FlexiOnboardingSummary {
  id: number;
  businessId: number;
  status: FlexiOnboardingStatus;
  /** ISO datetime string. */
  createdOn: string;
  /** ISO date string (yyyy-MM-dd). Null until the form has been completed once. */
  validUntil: string | null;
  createdBy: number | null;
  createdByName: string | null;
  modifiedBy: number | null;
  modifiedByName: string | null;
  /** ISO datetime string. */
  modifiedOn: string | null;
  /** ISO datetime string. */
  completedOn: string | null;
}

/** Matches FlexiOnboardingResponse. */
export interface FlexiOnboardingAggregate {
  id: number;
  businessId: number;
  status: FlexiOnboardingStatus;
  currentStep: number;
  /** ISO date string (DateOnly serializes as `YYYY-MM-DD`). Null until first PDF generation. */
  validUntil: string | null;
  /**
   * True when this is the business's earliest form — the original onboarding.
   * Later forms are periodic re-assessments and are worded as such on completion.
   * An unsaved draft carries id 0; it is `true` only for a business with no forms.
   */
  isFirstForm: boolean;
  steps: FlexiOnboardingStep[];
}

/** Classification of a step comment. Mirrors FlexiOnboardingCommentType (BE enum). */
export enum FlexiOnboardingCommentType {
  Note = 0,
  Action = 1,
  Decision = 2,
}

/** Who owns an Action comment. Mirrors FlexiOnboardingResponsibleParty (BE enum). */
export enum FlexiOnboardingResponsibleParty {
  Customer = 0,
  Advisor = 1,
}

/** Matches FlexiOnboardingStepCommentResponse. */
export interface FlexiOnboardingStepComment {
  id: number;
  commentText: string;
  isPublic: boolean;
  /** 0 = Note (default), 1 = Action, 2 = Decision. */
  commentType: FlexiOnboardingCommentType;
  /** 0 = Customer, 1 = Advisor. Null when not an Action. */
  responsibleParty: FlexiOnboardingResponsibleParty | null;
  /** ISO date string (yyyy-MM-dd) or null. Set only for Actions. */
  deadline: string | null;
  createdBy: number | null;
  createdByName: string | null;
  /** ISO datetime string. */
  createdOn: string;
}

/** Matches FlexiOnboardingFinalStepResponse — customer-facing read-only step. */
export interface FlexiOnboardingFinalStep {
  stepKey: string;
  position: number;
  title: string;
  shortLabel: string;
  subtitle: string | null;
  finalText: string | null;
}

/** Matches FlexiOnboardingMeetingResponse — cover-page metadata. */
export interface FlexiOnboardingMeeting {
  businessId: number;
  businessName: string;
  organisationNumber: string | null;
  advisorName: string | null;
  /** ISO datetime string. */
  completedOn: string | null;
  /** ISO date string (yyyy-MM-dd). */
  validUntil: string | null;
  agreementId: number | null;
}

/** Matches FlexiOnboardingFinalActionResponse — one agreed action. */
export interface FlexiOnboardingFinalAction {
  id: number;
  stepTitle: string;
  stepPosition: number;
  commentText: string;
  responsibleParty: FlexiOnboardingResponsibleParty | null;
  /** ISO date string (yyyy-MM-dd). */
  deadline: string | null;
  createdByName: string | null;
  createdOn: string;
}

/** Matches FlexiOnboardingFinalViewResponse — full customer doc bundle. */
export interface FlexiOnboardingFinalView {
  meeting: FlexiOnboardingMeeting | null;
  actions: FlexiOnboardingFinalAction[];
  steps: FlexiOnboardingFinalStep[];
}

/** Request bodies — match the BE Request DTOs. */
export interface UpdateOnboardingStepRequest {
  editableText: string | null;
}

export interface AddOnboardingCommentRequest {
  commentText: string;
  isPublic: boolean;
  /** 0 = Note (default), 1 = Action, 2 = Decision. */
  commentType?: FlexiOnboardingCommentType;
  /** 0 = Customer, 1 = Advisor. Only sent when commentType = Action. */
  responsibleParty?: FlexiOnboardingResponsibleParty | null;
  /** ISO date string yyyy-MM-dd. Only sent when commentType = Action. */
  deadline?: string | null;
}

export interface NewFlexiOnboardingStepRequest {
  insertAtPosition: number;
  title: string;
  shortLabel: string;
  subtitle?: string | null;
  templateText?: string | null;
}

/** Matches FlexiOnboardingRemovedDefaultStepResponse. */
export interface FlexiOnboardingRemovedDefaultStep {
  stepKey: string;
  title: string;
  shortLabel: string;
  subtitle: string | null;
}
